import { useState, useEffect, useCallback, useRef } from 'react';
import {
  GoogleMap,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Spinner } from "@/components/ui/spinner";
import { MapTrifold } from "@phosphor-icons/react";

interface Building {
  id: string;
  googlePlaceLongAddress?: string;
}

interface BuildingLocation {
  id: string;
  lat: number;
  lng: number;
}

interface ClusterMapProps {
  buildings: Building[];
  height?: string;
  interactive?: boolean;
  showPlaceholder?: boolean;
}

const defaultCenter = {
  lat: 50.85,
  lng: 4.35, // Belgium center
};

const GOOGLE_MAPS_API_KEY = "AIzaSyCRlKKWTlfsbs1bdQ6ziL9e9ptaiOJbnqI";

export function ClusterMap({
  buildings,
  height = "200px",
  interactive = true,
  showPlaceholder = true,
}: ClusterMapProps) {
  const [buildingLocations, setBuildingLocations] = useState<BuildingLocation[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // Geocode all building addresses
  const geocodeBuildings = useCallback(async () => {
    if (!isLoaded || !buildings.length) return;

    setIsGeocoding(true);
    const geocoder = new window.google.maps.Geocoder();
    const locations: BuildingLocation[] = [];

    for (const building of buildings) {
      if (!building.googlePlaceLongAddress) continue;

      try {
        const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode({ address: building.googlePlaceLongAddress }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results) {
              resolve(results);
            } else {
              reject(status);
            }
          });
        });

        const location = result[0].geometry.location;
        locations.push({
          id: building.id,
          lat: location.lat(),
          lng: location.lng(),
        });
      } catch (error) {
        console.error(`Failed to geocode ${building.googlePlaceLongAddress}:`, error);
      }
    }

    setBuildingLocations(locations);
    setIsGeocoding(false);
  }, [isLoaded, buildings]);

  useEffect(() => {
    geocodeBuildings();
  }, [geocodeBuildings]);

  // Fit map bounds to show all markers
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    fitBounds(map);
  }, [buildingLocations]);

  const fitBounds = useCallback((map: google.maps.Map) => {
    if (buildingLocations.length === 0) return;

    if (buildingLocations.length === 1) {
      map.setCenter({ lat: buildingLocations[0].lat, lng: buildingLocations[0].lng });
      map.setZoom(15);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    buildingLocations.forEach((location) => {
      bounds.extend({ lat: location.lat, lng: location.lng });
    });

    map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
  }, [buildingLocations]);

  // Update bounds when locations change
  useEffect(() => {
    if (mapRef.current && buildingLocations.length > 0) {
      fitBounds(mapRef.current);
    }
  }, [buildingLocations, fitBounds]);

  // Show placeholder if no buildings
  if (buildings.length === 0 && showPlaceholder) {
    return (
      <div
        className="flex items-center justify-center bg-muted/50 rounded-lg"
        style={{ height }}
      >
        <div className="text-center">
          <MapTrifold className="size-8 text-muted-foreground/30 mx-auto mb-2" weight="duotone" />
          <p className="text-xs text-muted-foreground">Geen locaties</p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (!isLoaded || isGeocoding) {
    return (
      <div
        className="flex items-center justify-center bg-muted/50 rounded-lg"
        style={{ height }}
      >
        <Spinner className="size-5 text-brick-primary" />
      </div>
    );
  }

  // Show placeholder if geocoding failed for all buildings
  if (buildingLocations.length === 0 && buildings.length > 0) {
    return (
      <div
        className="flex items-center justify-center bg-muted/50 rounded-lg"
        style={{ height }}
      >
        <div className="text-center">
          <MapTrifold className="size-8 text-muted-foreground/30 mx-auto mb-2" weight="duotone" />
          <p className="text-xs text-muted-foreground">Kaart niet beschikbaar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={defaultCenter}
        zoom={10}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: !interactive,
          zoomControl: interactive,
          scrollwheel: interactive,
          draggable: interactive,
          clickableIcons: false,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
          ],
        }}
      >
        {buildingLocations.map((location) => (
          <MarkerF
            key={location.id}
            position={{ lat: location.lat, lng: location.lng }}
            title={location.id}
          />
        ))}
      </GoogleMap>
    </div>
  );
}

export default ClusterMap;
